import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Loader2, XCircle, Zap } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { track } from '../lib/analytics';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { clear } = useCart();
  const [state, setState] = useState('checking'); // checking | paid | pending | error
  const [amount, setAmount] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    const sessionId = params.get('session_id');
    if (!sessionId) { setState('error'); return; }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await axios.get(`${API}/api/payments/status/${sessionId}`);
        if (data.payment_status === 'paid') {
          setAmount(data.amount);
          setState('paid');
          track('purchase', { value: data.amount });
          clear();
          return;
        }
        if (attempts.current >= 8) { setState('pending'); return; }
        timer = setTimeout(poll, 2000);
      } catch {
        if (attempts.current >= 8) { setState('error'); return; }
        timer = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [params, clear]);

  return (
    <div className="min-h-[80vh] bg-[#050505] flex items-center justify-center px-4" data-testid="payment-success-page">
      <div className="max-w-md w-full text-center bg-[#0d0d0d] border border-white/10 rounded-2xl p-10">
        {state === 'checking' && (
          <>
            <Loader2 className="animate-spin text-white/60 mx-auto" size={48} />
            <p className="text-white/70 mt-6 font-cond text-lg">Verifica del pagamento in corso…</p>
          </>
        )}
        {state === 'paid' && (
          <>
            <CheckCircle2 className="text-green-500 mx-auto" size={56} />
            <h1 className="text-white font-display text-3xl mt-5">GRAZIE!</h1>
            <p className="text-white/60 mt-3">Il tuo ordine è stato confermato{amount ? ` (€${amount.toFixed(2)})` : ''}. Riceverai una conferma via email.</p>
            <button onClick={() => navigate('/')} className="mt-7 inline-flex items-center gap-2 bg-red-600 hover:bg-red-500 text-white font-cond tracking-wide px-6 py-3 rounded-lg">
              <Zap size={18} /> Torna al sito
            </button>
          </>
        )}
        {state === 'pending' && (
          <>
            <Loader2 className="text-yellow-400 mx-auto" size={48} />
            <h1 className="text-white font-display text-2xl mt-5">Pagamento in elaborazione</h1>
            <p className="text-white/60 mt-3">Il pagamento è in fase di conferma. Controlla la tua email tra poco.</p>
            <Link to="/" className="mt-7 inline-block text-red-400 hover:text-red-300">Torna al sito</Link>
          </>
        )}
        {state === 'error' && (
          <>
            <XCircle className="text-red-500 mx-auto" size={52} />
            <h1 className="text-white font-display text-2xl mt-5">Qualcosa è andato storto</h1>
            <p className="text-white/60 mt-3">Non siamo riusciti a verificare il pagamento.</p>
            <Link to="/" className="mt-7 inline-block text-red-400 hover:text-red-300">Torna al sito</Link>
          </>
        )}
      </div>
    </div>
  );
}
